import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { marketplaceAPI } from '../../services/marketplaceAPI';
import ProductCard from '../../components/marketplace/ProductCard';
import SearchBar from '../../components/marketplace/SearchBar';
import Pagination from '../../components/marketplace/Pagination';
import DashboardButton from '../../components/DashboardButton';
import '../../styles/marketplace.css';
import './LabTestsPage.css';

const LabTestsPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    category: 'lab-test',
    search: '',
    page: 1,
    limit: 12,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [pagination, setPagination] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize theme
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const darkMode = savedTheme === 'dark';
    setIsDarkMode(darkMode);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    const theme = newDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await marketplaceAPI.getProducts(filters);
      
      if (response.success) {
        setProducts(response.data.products);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to fetch lab tests');
      }
    } catch (err) {
      console.error('Error fetching lab tests:', err);
      setError('Error loading lab tests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleSearch = (searchTerm) => {
    handleFilterChange({ search: searchTerm });
  };

  const handleMyLabTests = () => {
    navigate('/lab-tests/my-tests');
  };

  const handleMyReports = () => {
    navigate('/lab-tests/my-reports');
  };

  return (
    <div className="lab-tests-page min-h-screen">
      {/* Dark Mode Toggle Button */}
      <button
        onClick={toggleDarkMode}
        className="dark-mode-toggle-btn"
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {isDarkMode ? (
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      {/* Dashboard Button */}
      <DashboardButton userRole="Patient" />
      
      {/* Enhanced Modern Header */}
      <div className="lab-tests-header">
        {/* Background Pattern */}
        <div className="header-pattern"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          
          <div className="text-center mb-8">
            <h1 className="lab-tests-title">
              🧪 Lab Tests
            </h1>
            <p className="lab-tests-subtitle">
              Comprehensive diagnostic tests and health checkups
            </p>
            
            {/* Action Buttons */}
            <div className="header-action-buttons">
              <button
                onClick={handleMyLabTests}
                className="header-action-btn"
              >
                📋 My Lab Tests
              </button>
              
              <button
                onClick={handleMyReports}
                className="header-action-btn"
              >
                📄 My Reports
              </button>
            </div>
            
            {/* Enhanced Search Section */}
            <div className="header-search-section">
              <div className="search-section-content">
                <h3 className="search-section-title">
                  🔍 Search Lab Tests
                </h3>
                <SearchBar onSearch={handleSearch} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Enhanced Sidebar */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="filters-sidebar">
              <div className="filters-header">
                <div className="filters-icon">
                  <span>🧪</span>
                </div>
                <h3 className="filters-title">
                  Lab Test Filters
                </h3>
              </div>
              
              {/* Enhanced Sort Options */}
              <div className="filters-content">
                <h4 className="sort-title">
                  <span>📊</span>
                  Sort Tests
                </h4>
                <select
                  value={`${filters.sortBy}-${filters.sortOrder}`}
                  onChange={(e) => {
                    const [sortBy, sortOrder] = e.target.value.split('-');
                    handleFilterChange({ sortBy, sortOrder });
                  }}
                  className="sort-select"
                >
                  <option value="createdAt-desc">Newest First</option>
                  <option value="createdAt-asc">Oldest First</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="name-asc">Name: A to Z</option>
                  <option value="name-desc">Name: Z to A</option>
                </select>
              </div>

              {/* Enhanced Clear Filters */}
              {filters.search && (
                <button
                  onClick={() => setFilters(prev => ({ 
                    ...prev, 
                    search: '', 
                    page: 1 
                  }))}
                  className="clear-search-btn"
                >
                  Clear Search
                </button>
              )}
            </div>
          </div>

          {/* Enhanced Main Content */}
          <div className="flex-1">
            {error && (
              <div className="error-card">
                <div className="error-text">{error}</div>
              </div>
            )}

            {loading ? (
              <div className="loading-grid">
                {[...Array(6)].map((_, index) => (
                  <div key={index} className="skeleton-card">
                    <div className="skeleton-image"></div>
                    <div className="skeleton-content">
                      <div className="skeleton-line"></div>
                      <div className="skeleton-line-short"></div>
                      <div className="skeleton-line-large"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="empty-state-card">
                <div className="empty-icon">🧪</div>
                <div className="empty-text">
                  {filters.search ? 
                    'No lab tests found matching your search.' : 
                    'No lab tests available.'
                  }
                </div>
                {filters.search && (
                  <button
                    onClick={() => setFilters(prev => ({ 
                      ...prev, 
                      search: '', 
                      page: 1 
                    }))}
                    className="btn-clear-search"
                  >
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Enhanced Results Info */}
                <div className="results-info-card">
                  <div className="results-text">
                    Showing {((pagination.currentPage - 1) * filters.limit) + 1} to{' '}
                    {Math.min(pagination.currentPage * filters.limit, pagination.totalProducts)} of{' '}
                    <span className="results-count">{pagination.totalProducts}</span> lab tests
                  </div>
                  <div className="results-page-badge">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </div>
                </div>

                {/* Enhanced Products Grid */}
                <div className="products-grid">
                  {products.map(product => (
                    <ProductCard key={product._id} product={product} />
                  ))}
                </div>

                {/* Enhanced Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="pagination-container">
                    <Pagination
                      currentPage={pagination.currentPage}
                      totalPages={pagination.totalPages}
                      onPageChange={handlePageChange}
                      hasNextPage={pagination.hasNextPage}
                      hasPrevPage={pagination.hasPrevPage}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabTestsPage;
